import { useCallback, useEffect, useRef } from "react";
/**
 * Shown-and-exit tracking for a dialog, with the latch that makes the exit
 * count correct.
 *
 * The latch is the whole point: both apps' dialogs call `onClose` after
 * `onNegative`/`onPositive`, and a CTA that navigates then closes — so an
 * untracked close handler would report a dismissal behind every deliberate
 * choice. One exit per dialog instance, whichever happens first.
 *
 * Mounted once per open (each app renders one dialog at a time), so the shown
 * event needs no latch of its own.
 *
 * Generic because the credit gates and the club-import nudges are separate
 * funnels with different props, but must not drift on what "an exit" means.
 */
export const useDialogExitTracking = (params, trackers) => {
    const exited = useRef(false);
    const latest = useRef(params);
    const track = useRef(trackers);
    // In an effect, not the render body: writing a ref while rendering is a rule
    // violation. Declared before the shown effect so the sync runs first.
    useEffect(() => {
        latest.current = params;
        track.current = trackers;
    }, [params, trackers]);
    useEffect(() => {
        track.current.onShown(latest.current);
    }, []);
    const trackExit = useCallback((value) => {
        if (exited.current)
            return;
        exited.current = true;
        track.current.onExit({ ...latest.current, value });
    }, []);
    return { trackExit };
};
//# sourceMappingURL=useDialogExitTracking.js.map